import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import QuickItemViewBox from "../../components/DataDisplayer/QuickItemViewBox";
import { GridDataColumns, IGridDataConfig, IModalObject } from "../../components/GridData";
import BaseApp, { IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional } from "../BaseApp";
import WarehousesApp from "../warehouses/WarehousesApp";
import { useReturnAllWarehouses } from "../warehouses/warehousesSlice";
import { addNewSection, deleteSection, setSelectedSections, updateAllSections, updateSection, updateSectionsLoadingStatus, updateSectionsPagination, updateSectionsSearch, updateSectionsSearchParams, useReturnAllSections, useReturnSectionsLoadingStatus, useReturnSectionsPagination, useReturnSectionsSearch, useReturnSectionsSearchParams, useReturnSelectedSections } from "./sectionsSlice";
import { ICellRendererParams } from "ag-grid-community";
import { Link } from "react-router-dom";

/**
 * Section Model
 */
export interface ISectionModel{
    id: number,
    title: string,
    slug: string,
    thumbnail: string,
    created_by: string,
    date_created: string,
    last_updated: string,
    is_active: false,
    warehouse_id: number,
    warehouse_title: string
}

export const EmptySectionModel: ISectionModel = {
    id: 0,
    title: "",
    slug: "",
    thumbnail: "",
    created_by: "",
    date_created: "",
    last_updated: "",
    is_active: false,
    warehouse_id: 0,
    warehouse_title: ""
}

/**
 * Sections App
 */

export default class SectionsApp extends BaseApp {

    appName = "sections"
    appBaseEndPoint = "/warehouses/sections"

    customListEndPoint: string = this.appBaseEndPoint

    elemPrefix: string = "sections_"

    messages = {
        create: "Create a new section",
        createShort: "New section",
        edit: "Edit section",
        editShort: "Edit",
        delete: "Delete section",
        deleteShort: "Delete",
        view: "View section",
        nothingFound: "No sections have been found.",
    }

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "thumbnail",
                type: "thumbnail",
                label: "Thumbnail"
            },
            {
                id: "title",
                type: "text",
                label: "Title"
            },
            {
                id: "warehouse_title",
                type: "text",
                label: "Warehouse",
                quickItemView : (item, shown, handleHide) => <QuickItemViewBox shown={shown} handleHide={handleHide} itemsList={useReturnAllWarehouses} itemId={item.warehouse_id} baseApp={new WarehousesApp()} />
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No sections have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "sections_form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 8,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "warehouse_id",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Warehouse",
                    fullWidth: true,
                    appClass: new WarehousesApp(),
                    selectLabel: "Select Warehouse",
                    gridColumns: new WarehousesApp().gridDataColumns
                },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Title, warehouse, etc."
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
            {
                id: this.elemPrefix + "warehouse_id",
                message: "Warehouse is required!"
            }
        ]
    }

   /**
     * Dynamic base page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllSections,
        pagination: useReturnSectionsPagination,
        loading: useReturnSectionsLoadingStatus,
        search: useReturnSectionsSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateSectionsLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteSection({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewSection((item as ISectionModel))),
        updateItem: (item, dispatch) => dispatch(updateSection((item as ISectionModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllSections((items as ISectionModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateSectionsSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateSectionsPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/sections/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/sections/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
    }

    /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        {
            field: "title",
            headerName: "Section",
            type: "title",
            width: 250,
            minWidth: 250,
            cellRendererParams: {
                appClass: () => new SectionsApp(),
                enableQuickPreview: true,
            }
        },
        // {
        //     field: "warehouse_title",
        //     headerName: "Warehouse",
        //     cellRenderer: (params: ICellRendererParams<ISectionModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/warehouses/view/${item.warehouse_id}`}>
        //                     <h3 className="title">{item.warehouse_title}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
        {
            field: "warehouse_title",
            headerName: "Warehouse",
            type: "quick-preview",
            width: 300,
            minWidth: 300,
            cellRendererParams: {
                appClass: () => new WarehousesApp(),
                elemIdTitle: "warehouse_id",
            }
        },
        {
            field: "date_created",
            headerName: "Date Created",
            type: "date",
            width: 250,
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/sections/view/[id]",
        editItemLink: "/panel/sections/edit/[id]",
        filterForm: {
            name: "sections-data-displayer-filter-form",
            elements: [
                {
                    id: "warehouse_id",
                    xs: 12,
                    type: "DynamicModelSelect",
                    label: "Warehouse",
                    fullWidth: true,
                    appClass: new WarehousesApp(),
                    selectLabel: "Select Warehouse",
                    gridColumns: new WarehousesApp().gridDataColumns
                },
            ]
        }
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedSections,
        searchParams: useReturnSectionsSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedSections(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updateSectionsSearchParams(params))
    }

}